import Form from '@rjsf/core'
import { fields, templates, widgets } from '../ui-schema-components'
import validator from '@rjsf/validator-ajv8'
import {
  CheckIcon,
  CreateProduct,
  ModalDialog,
  Mtds,
  Product,
  useCreateProductMutation,
  useErrorToast
} from '../../index'
import type { RJSFSchema } from '@rjsf/utils'
import { useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

const productSchema: RJSFSchema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      title: 'Navn'
    },
    code: {
      type: 'string',
      title: 'SSD2-kode'
    }
  },
  required: ['name', 'code']
}

type CreateProductModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  onCreated?: (product: Product) => void
}

export const CreateProductModal = ({
  isOpen,
  setOpen,
  onCreated
}: CreateProductModalProps) => {
  const createProduct = useCreateProductMutation()
  const queryClient = useQueryClient()
  const currentDataRef = useRef<CreateProduct | undefined>(undefined)
  const showError = useErrorToast('opprette produktet')
  const onSubmit = async (
    formData: CreateProduct | undefined
  ): Promise<void> => {
    if (formData != null) {
      try {
        const created = await createProduct.mutateAsync(formData)
        if (onCreated) {
          await queryClient.refetchQueries({ queryKey: ['batch'] })
          onCreated(created)
        }
        Mtds.toast.success('Produktet er lagt til')
        setOpen(false)
      } catch (error) {
        showError(error)
      }
    }
  }
  return (
    <ModalDialog
      id='create-product-modal'
      open={isOpen}
      onClose={(e) => {
        e.stopPropagation()
        setOpen(false)
      }}
    >
      <Mtds.Button
        command='close'
        commandfor={'create-product-modal'}
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Legg til produkt</Mtds.Heading>
        <Form
          tagName={'div'}
          noHtml5Validate
          schema={productSchema}
          widgets={widgets}
          fields={fields}
          templates={templates}
          showErrorList={false}
          validator={validator}
          onChange={(e) => {
            currentDataRef.current = e.formData as CreateProduct | undefined
          }}
        >
          <Mtds.Flex className='mt-8'>
            <Mtds.Button type='button' onClick={() => setOpen(false)}>
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='button'
              onClick={async () => onSubmit(currentDataRef.current)}
            >
              <CheckIcon />
              Lagre
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}
